import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Loader, Info } from 'lucide-react';
import { API_ENDPOINTS } from '../config';

// Fallback copy icon
const CopyIcon = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

import axios from 'axios';

const EditDatasourceModal = ({ isOpen, onClose, onDatasourceUpdated, datasource, user }) => {
  const [name, setName] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pythonPath, setPythonPath] = useState('');
  const [pyodbcDrivers, setPyodbcDrivers] = useState(null);
  const [showLocalTestHelpModal, setShowLocalTestHelpModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSuggestedModal, setShowSuggestedModal] = useState(false);
  const [suggestedString, setSuggestedString] = useState('');
  const [suggestedCopied, setSuggestedCopied] = useState(false);
  const [mssqlCopied, setMssqlCopied] = useState(false);

  // Privacy field (for admin/owner)
  const [privacy, setPrivacy] = useState('private');

  // Read-only mode (default true for safety)
  const [readOnly, setReadOnly] = useState(true);

  // PostgreSQL fields
  const [postgresConnectionString, setPostgresConnectionString] = useState('');

  // MySQL fields
  const [mysqlInputMode, setMysqlInputMode] = useState('string');
  const [mysqlConnectionString, setMysqlConnectionString] = useState('');
  const [mysqlHost, setMysqlHost] = useState('');
  const [mysqlPort, setMysqlPort] = useState('3306');
  const [mysqlDatabase, setMysqlDatabase] = useState('');
  const [mysqlUser, setMysqlUser] = useState('');
  const [mysqlPassword, setMysqlPassword] = useState('');

  // MSSQL fields
  const [mssqlConnectionString, setMssqlConnectionString] = useState('');

  // Snowflake fields
  const [snowflakeConnectionString, setSnowflakeConnectionString] = useState('');

  // Database type (read-only)
  const dbType = datasource?.db_type;

  // Initialize form with datasource data
  useEffect(() => {
    if (datasource && isOpen) {
      setName(datasource.name || '');
      setPrivacy(datasource.privacy || 'private');
      setReadOnly(datasource.read_only !== false); // Default to true if undefined

      const connString = datasource.connection_string || '';

      if (dbType === 'postgresql') {
        setPostgresConnectionString(connString);
      } else if (dbType === 'mysql') {
        setMysqlConnectionString(connString);
        setMysqlInputMode('string');
      } else if (dbType === 'mssql') {
        setMssqlConnectionString(connString);
      } else if (dbType === 'snowflake') {
        setSnowflakeConnectionString(connString);
      }
    }
  }, [datasource, isOpen, dbType]);

  const resetForm = () => {
    setName('');
    setTesting(false);
    setTestResult(null);
    setSaving(false);
    setPrivacy('private');
    setReadOnly(true);
    setPostgresConnectionString('');
    setMysqlInputMode('string');
    setMysqlConnectionString('');
    setMysqlHost('');
    setMysqlPort('3306');
    setMysqlDatabase('');
    setMysqlUser('');
    setMysqlPassword('');
    setMssqlConnectionString('');
    setSnowflakeConnectionString('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Close modals on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showLocalTestHelpModal) {
          setShowLocalTestHelpModal(false);
        } else if (showSuggestedModal) {
          setShowSuggestedModal(false);
        } else {
          handleClose();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, showLocalTestHelpModal, showSuggestedModal]);

  // Fetch backend env info
  useEffect(() => {
    if (!isOpen) return;
    const fetchEnvInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API_ENDPOINTS.main}/datasources/env-info`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setPythonPath(resp.data?.python_executable || 'python');
        setPyodbcDrivers(resp.data?.pyodbc_drivers || null);
      } catch (e) {
        setPythonPath('python');
        setPyodbcDrivers(null);
      }
    };
    fetchEnvInfo();
  }, [isOpen]);

  const buildLocalTestCommand = () => {
    const py = pythonPath || 'python';
    if (dbType === 'postgresql') {
      const s = postgresConnectionString || '';
      return `${py} -c "import sys, psycopg2; print('Python:', sys.executable); s=r'${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}'; c=psycopg2.connect(s); print('OK'); c.close()"`;
    }
    if (dbType === 'mysql') {
      if (mysqlInputMode === 'string') {
        const s = mysqlConnectionString || '';
        return `${py} -c "import sys, pymysql, urllib.parse as u; print('Python:', sys.executable); s=r'${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}'; p=u.urlparse(s); c=pymysql.connect(host=p.hostname or 'localhost', port=p.port or 3306, user=u.unquote(p.username or ''), password=u.unquote(p.password or ''), database=(p.path or '/').lstrip('/')); print('OK'); c.close()"`;
      }
      return `${py} -c "import sys, pymysql; print('Python:', sys.executable); c=pymysql.connect(host='${mysqlHost}', port=${parseInt(mysqlPort||'3306')}, user='${mysqlUser}', password='${mysqlPassword}', database='${mysqlDatabase}'); print('OK'); c.close()"`;
    }
    if (dbType === 'mssql') {
      const s = mssqlConnectionString || '';
      return `${py} -c "import sys, pyodbc; print('Python:', sys.executable); print('Drivers:', pyodbc.drivers()); s=r'${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}'; c=pyodbc.connect(s); print('OK'); c.close()"`;
    }
    return '';
  };

  const buildPythonSuggestedSnippet = () => {
    const s = suggestedString || '';
    const safe = s.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\"/g, '\\"');
    return `import pyodbc\nconn = pyodbc.connect(r'${safe}')`;
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const payload = { db_type: dbType };

      if (dbType === 'postgresql') {
        payload.connection_string = postgresConnectionString;
      } else if (dbType === 'mysql') {
        if (mysqlInputMode === 'string') {
          payload.connection_string = mysqlConnectionString;
        } else {
          payload.host = mysqlHost;
          payload.port = parseInt(mysqlPort);
          payload.database = mysqlDatabase;
          payload.user = mysqlUser;
          payload.password = mysqlPassword;
        }
      } else if (dbType === 'mssql') {
        payload.connection_string = mssqlConnectionString;
      }

      const response = await axios.post(
        `${API_ENDPOINTS.main}/datasources/test`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      setTestResult({
        success: response.data.success,
        message: response.data.message,
        suggested_connection_string: response.data.suggested_connection_string || null
      });

      if (response.data.success) {
        setTimeout(() => setTestResult(null), 5000);
      }

      if (response.data.success && response.data.suggested_connection_string && dbType === 'mssql') {
        setMssqlConnectionString(response.data.suggested_connection_string);
        setSuggestedString(response.data.suggested_connection_string);
        setShowSuggestedModal(true);
      }
    } catch (error) {
      let errorMessage = 'Connection test failed';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out after 10 seconds';
      } else if (error.response) {
        errorMessage = error.response.data?.detail || error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        errorMessage = error.message || 'Connection test failed';
      }

      setTestResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const token = localStorage.getItem('token');

      // Build update payload
      const payload = {
        name: name,
        privacy: privacy,
        read_only: readOnly
      };

      if (dbType === 'postgresql') {
        payload.connection_string = postgresConnectionString;
      } else if (dbType === 'mysql') {
        if (mysqlInputMode === 'string') {
          payload.connection_string = mysqlConnectionString;
        } else {
          if (!mysqlHost || !mysqlDatabase || !mysqlUser) {
            alert('MySQL host, database, and user are required');
            setSaving(false);
            return;
          }
          payload.host = mysqlHost;
          payload.port = parseInt(mysqlPort);
          payload.database = mysqlDatabase;
          payload.user = mysqlUser;
          payload.password = mysqlPassword;
        }
      } else if (dbType === 'mssql') {
        payload.connection_string = mssqlConnectionString;
      } else if (dbType === 'snowflake') {
        payload.connection_string = snowflakeConnectionString;
      }

      // Call PUT endpoint to update datasource
      await axios.put(
        `${API_ENDPOINTS.main}/datasources/${datasource.datasource_id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Clear Taurus config cache (fire-and-forget) - ensures fresh credentials are used
      // This is critical when updating datasources with new passwords
      fetch(`${API_ENDPOINTS.taurus}/api/clear-cache`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {}); // Fire-and-forget, don't block on errors

      // Notify parent component
      if (onDatasourceUpdated) {
        onDatasourceUpdated();
      }

      handleClose();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update datasource');
      setSaving(false);
    }
  };

  if (!isOpen || !datasource) return null;

  const dbTypeName = dbType === 'postgresql' ? 'PostgreSQL' : dbType === 'mysql' ? 'MySQL' : dbType === 'snowflake' ? 'Snowflake' : 'MSSQL';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit {dbTypeName} Datasource
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Connection Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="My Database"
              />
            </div>

            {/* Privacy + Access Mode Row */}
            <div className="flex gap-4">
              {/* Privacy Field (for admin/owner only) */}
              {user && (user.role === 'admin' || user.role === 'owner') && (
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Privacy
                  </label>
                  <select
                    value={privacy}
                    onChange={(e) => setPrivacy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="private">Private</option>
                    <option value="published">
                      {user.role === 'owner' ? 'Publish (Demo)' : 'Publish (Org)'}
                    </option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {privacy === 'private' ? 'Only visible to you' : user.role === 'owner' ? 'Visible to all users' : 'Visible to your org'}
                  </p>
                </div>
              )}

              {/* Read-Only Access Mode */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Access Mode
                </label>
                <div className="flex items-center space-x-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="readOnly"
                      checked={readOnly}
                      onChange={() => setReadOnly(true)}
                      className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                    />
                    <span className="ml-1.5 text-sm text-gray-700 dark:text-gray-300">Read-only</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="readOnly"
                      checked={!readOnly}
                      onChange={() => setReadOnly(false)}
                      className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                    />
                    <span className="ml-1.5 text-sm text-gray-700 dark:text-gray-300">Full access</span>
                  </label>
                </div>
                <p className={`mt-1 text-xs ${readOnly ? 'text-gray-500 dark:text-gray-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {readOnly
                    ? '🔒 Blocks INSERT, UPDATE, DELETE, DROP, and other write operations.'
                    : '⚠️ Allows data modification. Use with caution on production databases.'}
                </p>
              </div>
            </div>

            {/* PostgreSQL Configuration */}
            {dbType === 'postgresql' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Connection String
                </label>
                <input
                  type="text"
                  value={postgresConnectionString}
                  onChange={(e) => setPostgresConnectionString(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="postgresql://user:password@host:port/database"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Format: postgresql://username:password@hostname:port/database
                </p>
              </div>
            )}

            {/* MSSQL Configuration */}
            {dbType === 'mssql' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    MSSQL Connection String
                  </label>
                  {Boolean(suggestedString) && (
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(mssqlConnectionString || ''); setMssqlCopied(true); setTimeout(() => setMssqlCopied(false), 1500); }}
                      className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
                      title="Copy successful string"
                    >
                      {mssqlCopied ? (<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />) : (<CopyIcon className="h-4 w-4" />)}
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={mssqlConnectionString}
                  onChange={(e) => setMssqlConnectionString(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="mssql://username:password@hostname:1433/database"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <strong>URL Format (Recommended):</strong> mssql://username:password@hostname:1433/database<br/>
                  <span className="text-gray-400 dark:text-gray-500">ODBC Format: Driver={'{ODBC Driver 17 for SQL Server}'};Server=...;Database=...;Uid=...;Pwd=...</span>
                </p>
              </div>
            )}

            {/* Snowflake Configuration */}
            {dbType === 'snowflake' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Snowflake Connection String
                </label>
                <input
                  type="text"
                  value={snowflakeConnectionString}
                  onChange={(e) => setSnowflakeConnectionString(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="snowflake://user:password@account/database/schema?warehouse=WH&role=ROLE"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <strong>Format:</strong> snowflake://user:password@account/database/schema?warehouse=WH&role=ROLE<br/>
                  <span className="text-gray-400 dark:text-gray-500">Account: Use the account identifier (e.g., DYYVDEC-PUB36339)</span>
                </p>
              </div>
            )}

            {/* MySQL Configuration */}
            {dbType === 'mysql' && (
              <div className="space-y-4">
                {/* Input Mode Toggle */}
                <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setMysqlInputMode('string')}
                    className={`pb-2 px-1 ${
                      mysqlInputMode === 'string'
                        ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    Connection String
                  </button>
                  <button
                    onClick={() => setMysqlInputMode('individual')}
                    className={`pb-2 px-1 ${
                      mysqlInputMode === 'individual'
                        ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    Individual Parameters
                  </button>
                </div>

                {mysqlInputMode === 'string' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Connection String
                    </label>
                    <input
                      type="text"
                      value={mysqlConnectionString}
                      onChange={(e) => setMysqlConnectionString(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="mysql://user:password@host:port/database"
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Format: mysql://username:password@hostname:port/database
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Host
                      </label>
                      <input
                        type="text"
                        value={mysqlHost}
                        onChange={(e) => setMysqlHost(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="localhost or sql.example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Port
                      </label>
                      <input
                        type="number"
                        value={mysqlPort}
                        onChange={(e) => setMysqlPort(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="3306"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Database
                      </label>
                      <input
                        type="text"
                        value={mysqlDatabase}
                        onChange={(e) => setMysqlDatabase(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="my_database"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        User
                      </label>
                      <input
                        type="text"
                        value={mysqlUser}
                        onChange={(e) => setMysqlUser(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="username"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        value={mysqlPassword}
                        onChange={(e) => setMysqlPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Test Result */}
            {testResult && (
              <div className="fixed top-4 right-4 z-50 max-w-md animate-slide-in">
                <div className={`p-4 rounded-lg shadow-lg ${testResult.success ? 'bg-green-50 dark:bg-green-900/90 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-700'}`}>
                  <div className="flex items-start">
                    {testResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="ml-3 flex-1">
                      <p className={`text-sm font-medium ${testResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                        {testResult.message}
                      </p>
                    </div>
                    <button
                      onClick={() => setTestResult(null)}
                      className={`ml-3 flex-shrink-0 ${testResult.success ? 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300' : 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300'}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Help & Tips Button */}
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowLocalTestHelpModal(true)}
                className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                <Info className="h-4 w-4 mr-2" />
                Help & Tips
              </button>
            </div>

            {/* Help & Tips Modal */}
            {showLocalTestHelpModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Help & Tips</h3>
                    <button onClick={() => setShowLocalTestHelpModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="h-5 w-5" /></button>
                  </div>
                  <div className="p-4 text-sm text-gray-700 dark:text-gray-300 space-y-4">
                    <div>
                      <strong className="block mb-2">Local Test Command (runs in your console)</strong>
                      <div className="flex items-start space-x-2">
                        <textarea
                          readOnly
                          value={buildLocalTestCommand()}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-xs h-20"
                        />
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(buildLocalTestCommand()); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                          className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
                          title="Copy"
                        >
                          {copied ? (<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />) : (<CopyIcon className="h-4 w-4" />)}
                        </button>
                      </div>
                      {pythonPath && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Python: {pythonPath}</p>
                      )}
                    </div>

                    <div>
                      <strong>How to use:</strong>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        <li>Copy the command and paste it into your terminal/PowerShell.</li>
                        <li>It runs with the same Python the app uses and prints OK on success.</li>
                        <li>After OK, click "Test Connection" and then "Update Datasource".</li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={() => setShowLocalTestHelpModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Close</button>
                  </div>
                </div>
              </div>
            )}

            {/* Suggested Connection String Modal */}
            {showSuggestedModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recommended Connection String</h3>
                    <button onClick={() => setShowSuggestedModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="h-5 w-5" /></button>
                  </div>
                  <div className="p-4 text-sm text-gray-700 dark:text-gray-300 space-y-3">
                    <p>We tested an alternative that succeeded and applied it to your input. You can copy the successful string for future reference:</p>
                    <div>
                      <div className="text-xs mb-1 text-gray-600 dark:text-gray-400">ODBC connection string</div>
                      <div className="flex items-start space-x-2">
                        <textarea readOnly value={suggestedString} className="flex-1 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-xs h-20" />
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(suggestedString); setSuggestedCopied(true); setTimeout(() => setSuggestedCopied(false), 1500); }}
                          className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
                          title="Copy"
                        >
                          {suggestedCopied ? (<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />) : (<CopyIcon className="h-4 w-4" />)}
                        </button>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs mb-1 text-gray-600 dark:text-gray-400">Python snippet (pyodbc)</div>
                      <div className="flex items-start space-x-2">
                        <textarea readOnly value={buildPythonSuggestedSnippet()} className="flex-1 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-xs h-24" />
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(buildPythonSuggestedSnippet()); setSuggestedCopied(true); setTimeout(() => setSuggestedCopied(false), 1500); }}
                          className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
                          title="Copy"
                        >
                          {suggestedCopied ? (<CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />) : (<CopyIcon className="h-4 w-4" />)}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={() => setShowSuggestedModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Close</button>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleTestConnection}
                disabled={testing || !name.trim()}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {testing ? (
                  <>
                    <Loader className="animate-spin h-4 w-4 mr-2" />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin h-4 w-4 mr-2" />
                    Updating...
                  </>
                ) : (
                  'Update Datasource'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDatasourceModal;
